import { Navigate, useLocation } from 'react-router-dom';
import { useAuth } from '@/hooks/useAuth';
import type { ReactNode } from 'react';
import type { UserRole } from '@/types';
import styles from './ProtectedRoute.module.css';

interface ProtectedRouteProps {
  children: ReactNode;
  /** If provided, only users with one of these roles can access the route. */
  allowedRoles?: UserRole[];
  /** Where to redirect unauthorized (but authenticated) users. Defaults to /unauthorized. */
  redirectTo?: string;
}

/**
 * Wraps routes that require authentication and optional role-based authorization.
 *
 * - Shows a loading spinner while checking auth state.
 * - Redirects to /login if user is not authenticated.
 * - Redirects patients to /portal/dashboard if they try to access admin routes.
 * - Redirects to /unauthorized if user lacks required roles.
 * - Preserves the intended destination in location state for post-login redirect.
 */
export function ProtectedRoute({ children, allowedRoles, redirectTo }: ProtectedRouteProps) {
  const { isAuthenticated, isLoading, userHasAnyRole, userHasRole } = useAuth();
  const location = useLocation();

  if (isLoading) {
    return (
      <div className={styles.loadingContainer} role="status" aria-label="Verificando autenticação">
        <div className={styles.spinner} aria-hidden="true" />
        <p className={styles.loadingText}>Verificando autenticação...</p>
      </div>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  // Role-based access control
  if (allowedRoles && !userHasAnyRole(allowedRoles)) {
    // Patients trying to access admin area → redirect to portal
    if (userHasRole('Patient')) {
      return <Navigate to="/portal/dashboard" replace />;
    }
    return <Navigate to={redirectTo ?? '/unauthorized'} replace />;
  }

  return <>{children}</>;
}
